import React from 'react';
import {
  Listbox,
  ListboxButton,
  ListboxOption,
  ListboxOptions,
} from '@headlessui/react';
import { Check, ChevronDown } from 'lucide-react';
import { cn } from '../../../utils/cn';
import { CellShell } from '../ImportGreenBeanModal/cells';
import { ROAST_TYPES, type RoastType } from './types';

// Re-export shared cells from the GB module so RB-modal consumers
// import everything from one path.
export { CellShell, TextCell, NumberCell, RupiahCell } from '../ImportGreenBeanModal/cells';

interface SourceGreenBeanCellProps {
  label: string;
  hasError: boolean;
  errorMessage?: string;
  testId: string;
}

export const SourceGreenBeanCell: React.FC<SourceGreenBeanCellProps> = ({
  label,
  hasError,
  errorMessage,
  testId,
}) => (
  <CellShell hasError={hasError} errorMessage={errorMessage}>
    <div
      data-test-id={testId}
      title="To change the source green bean, fix the row in Excel and re-upload."
      className="w-full bg-transparent px-2.5 py-1.5 text-sm text-gray-700"
    >
      {label || <span className="text-gray-400">— missing —</span>}
    </div>
  </CellShell>
);

interface RoastTypeCellProps {
  value: RoastType | '';
  onChange: (v: RoastType) => void;
  hasError: boolean;
  errorMessage?: string;
  testId: string;
}

export const RoastTypeCell: React.FC<RoastTypeCellProps> = ({
  value,
  onChange,
  hasError,
  errorMessage,
  testId,
}) => {
  const isValid = (ROAST_TYPES as readonly string[]).includes(value);
  return (
    <Listbox value={isValid ? (value as RoastType) : ''} onChange={(v) => onChange(v as RoastType)}>
      <div className="relative">
        <CellShell hasError={hasError} errorMessage={errorMessage}>
          <ListboxButton
            data-test-id={testId}
            className="relative w-full cursor-default bg-transparent py-1.5 pl-2.5 pr-8 text-left text-sm text-gray-900 outline-none"
          >
            <span className={cn('block truncate', !isValid && 'text-gray-400')}>
              {isValid ? value : 'Select…'}
            </span>
            <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
              <ChevronDown className="h-4 w-4 text-gray-400" aria-hidden="true" />
            </span>
          </ListboxButton>
        </CellShell>
        <ListboxOptions
          anchor="bottom start"
          className="z-[60] mt-1 max-h-60 w-[var(--button-width)] min-w-[8rem] overflow-auto rounded-md bg-white py-1 text-sm shadow-lg ring-1 ring-black/5 focus:outline-none"
        >
          {ROAST_TYPES.map((rt) => (
            <ListboxOption
              key={rt}
              value={rt}
              className={({ focus }) =>
                cn(
                  'relative cursor-default select-none py-1.5 pl-8 pr-3',
                  focus ? 'bg-primary-50 text-primary-900' : 'text-gray-900',
                )
              }
            >
              {({ selected }) => (
                <>
                  <span className={cn('block truncate', selected ? 'font-medium' : 'font-normal')}>
                    {rt}
                  </span>
                  {selected && (
                    <span className="absolute inset-y-0 left-0 flex items-center pl-2 text-primary-600">
                      <Check className="h-4 w-4" aria-hidden="true" />
                    </span>
                  )}
                </>
              )}
            </ListboxOption>
          ))}
        </ListboxOptions>
      </div>
    </Listbox>
  );
};
